import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export const ImportPreviewDialog = ({ preview, busy, fileName, onCancel, onConfirm }) => {
    const [excluded, setExcluded] = useState(new Set());
    useEffect(() => setExcluded(new Set()), [preview]);

    const rows = preview?.rows || [];
    const addCount = (preview?.to_add || 0) - excluded.size;
    const toggle = (row) => setExcluded((prev) => {
        const next = new Set(prev);
        next.has(row) ? next.delete(row) : next.add(row);
        return next;
    });

    return (
        <Dialog open={!!preview} onOpenChange={(o) => !o && !busy && onCancel()}>
            <DialogContent className="max-w-2xl" data-testid="import-preview-dialog">
                <DialogHeader>
                    <DialogTitle>Review import</DialogTitle>
                    <DialogDescription>
                        {fileName} — {preview?.to_add ?? 0} to add, {preview?.to_skip ?? 0} to skip. Untick any you want to leave out.
                    </DialogDescription>
                </DialogHeader>
                <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-border divide-y divide-border text-sm"
                    data-testid="import-preview-list">
                    {rows.length === 0 && <p className="px-3 py-4 text-muted-foreground">No rows found in this file.</p>}
                    {rows.map((r) => <PreviewRow key={r.row} r={r} checked={!excluded.has(r.row)} onToggle={toggle} />)}
                </div>
                <DialogFooter className="gap-2">
                    <Button variant="outline" className="rounded-full" onClick={onCancel} disabled={busy}
                        data-testid="import-preview-cancel-btn">Cancel</Button>
                    <Button className="rounded-full gap-2" disabled={busy || addCount <= 0}
                        onClick={() => onConfirm([...excluded])} data-testid="import-preview-confirm-btn">
                        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                        Import {addCount} record{addCount === 1 ? "" : "s"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const PreviewRow = ({ r, checked, onToggle }) => {
    const isAdd = r.status === "add";
    return (
        <label className={`flex items-center gap-3 px-3 py-2 ${isAdd ? "cursor-pointer" : "opacity-60"}`}
            data-testid={`import-preview-row-${r.row}`}>
            {isAdd ? (
                <Checkbox checked={checked} onCheckedChange={() => onToggle(r.row)}
                    data-testid={`import-preview-check-${r.row}`} />
            ) : <span className="w-4" />}
            <div className="min-w-0 flex-1">
                <p className="truncate">
                    <span className="text-muted-foreground">Row {r.row}: </span>
                    {r.artist || "—"} – {r.album_title || "—"}
                </p>
                {!isAdd && <p className="text-xs text-muted-foreground">{r.reason}</p>}
            </div>
            <span className={`text-xs shrink-0 ${isAdd ? "text-primary" : "text-muted-foreground"}`}
                data-testid={`import-preview-status-${r.row}`}>
                {isAdd ? (checked ? "Will add" : "Left out") : "Will skip"}
            </span>
        </label>
    );
};
